import { z } from 'zod';
/**
 * The single error shape for every endpoint (PLAN.md §14).
 *
 * Upstream CRM / Didit payloads are logged server-side against `correlationId`
 * and MUST NOT appear here — that is finding #9 in the legacy system, where
 * error responses leaked request bodies and upstream responses to the browser.
 */
export const ErrorBody = z.object({
    /** Stable machine-readable code. Clients branch on this, never on `message`. */
    code: z.string(),
    /** Human-readable, safe to display, already localised where relevant. */
    message: z.string(),
    /** Per-field validation messages, keyed by field name. */
    fields: z.record(z.array(z.string())).optional(),
    /** Ties this response to the server-side log line. */
    correlationId: z.string(),
});
/** Error codes shared between server and client. Extend as routes land. */
export const ErrorCode = {
    // generic
    BAD_REQUEST: 'bad_request',
    VALIDATION_FAILED: 'validation_failed',
    NOT_FOUND: 'not_found',
    /** The write collides with something that already exists — e.g. a taken host. */
    CONFLICT: 'conflict',
    RATE_LIMITED: 'rate_limited',
    INTERNAL: 'internal_error',
    // admin plane (PLAN.md §7.1)
    /** No Access JWT, or one that failed verification. */
    UNAUTHORIZED: 'unauthorized',
    /** A verified identity with no admins row, or a role below the route's floor. */
    FORBIDDEN: 'forbidden',
    // site resolution (PLAN.md §4.2)
    UNKNOWN_HOST: 'unknown_host',
    // email verification (PLAN.md §18.1)
    /** The bot check failed. The client should reset its widget and retry. */
    TURNSTILE_FAILED: 'turnstile_failed',
    /**
     * Every provider in the chain refused the message. Surfaced deliberately:
     * a uniform 200 here would leave the user waiting for a code that is never
     * going to arrive.
     */
    OTP_DELIVERY_FAILED: 'otp_delivery_failed',
    /** Wrong code. `fields.pin` carries the remaining attempts for the UI. */
    OTP_INVALID: 'otp_invalid',
    /** Unknown or timed-out challenge. Also returned for a replayed one, so a
     * used challenge is indistinguishable from one that never existed. */
    OTP_EXPIRED: 'otp_expired',
    /** The attempt cap was reached and the challenge was destroyed. */
    OTP_LOCKED: 'otp_locked',
    // login and password reset (PLAN.md §10)
    /**
     * The CRM refused the credentials. Deliberately one code for every way it can
     * refuse: an unknown address, a wrong password and a disabled account are
     * indistinguishable from here, or the login form becomes the account-existence
     * oracle that §10.2 exists to close (#17).
     */
    INVALID_CREDENTIALS: 'invalid_credentials',
    /** The second factor was wrong. `fields.code` carries what is left of the cap. */
    TWO_FACTOR_INVALID: 'two_factor_invalid',
    /**
     * The half-finished login behind a `challengeId` is gone — timed out, spent,
     * or capped. The client starts the login again rather than the second step.
     */
    TWO_FACTOR_EXPIRED: 'two_factor_expired',
    // session (PLAN.md §12)
    /** No session cookie, or one that has expired. The client restarts at step 1. */
    SESSION_REQUIRED: 'session_required',
    // account creation (PLAN.md §13.1)
    /**
     * The CRM already holds a full client for this address, so there is no lead
     * to create. Safe to say plainly — the caller passed the OTP, so they own the
     * address and this is not an enumeration channel (#17). The UI sends them to
     * /login rather than leaving them stuck on a form that cannot succeed.
     */
    ACCOUNT_EXISTS: 'account_exists',
    /** The CRM rejected the lead. `fields` carries its per-field messages. */
    CRM_VALIDATION: 'crm_validation',
    /** The chosen country is not one we can onboard from. */
    COUNTRY_NOT_PERMITTED: 'country_not_permitted',
    /**
     * A typed partner code matched neither a referral link nor a partner with a
     * usable link. Said plainly: the code is the partner's own, printed on their
     * material, and telling someone it is wrong is not an enumeration channel.
     */
    PARTNER_NOT_FOUND: 'partner_not_found',
    // identity verification (PLAN.md §15)
    /**
     * Verification was requested before step 1 produced a CRM lead. Didit keys
     * its session on `vendor_data = crmClientId`, so there is nothing to key on
     * yet — the client should finish step 1 first.
     */
    KYC_NOT_READY: 'kyc_not_ready',
    // compliance and completion (PLAN.md §13, §27.3)
    /**
     * The session is not at this step yet — an AML submission before identity
     * was approved, or a completion before the declaration was filed. The client
     * is told which step it should be on and sends the user there, rather than
     * being left on a form that cannot succeed.
     */
    STEP_NOT_AVAILABLE: 'step_not_available',
    // upstream
    CRM_UNAVAILABLE: 'crm_unavailable',
    KYC_UNAVAILABLE: 'kyc_unavailable',
};
//# sourceMappingURL=error.js.map